import { create } from 'zustand';

const STORAGE_KEY = 'cloudmaster_cookie_consent';

interface ConsentPreferences {
  analytics: boolean;
  advertising: boolean;
}

interface ConsentState {
  /** Whether the user has made a consent choice (dismiss the banner) */
  consentGiven: boolean;
  /** Whether the user has consented to analytics cookies */
  analytics: boolean;
  /** Whether the user has consented to advertising cookies (e.g. AdSense) */
  advertising: boolean;

  /** Accept all optional cookie categories */
  acceptAll: () => void;
  /** Reject all optional cookies — essential only */
  rejectAll: () => void;
  /** Alias for rejectAll: only essential cookies */
  acceptEssentialOnly: () => void;
  /** Update individual preferences */
  updatePreferences: (prefs: Partial<ConsentPreferences>) => void;
  /** Reset consent (e.g. user wants to change their mind) */
  resetConsent: () => void;
}

function loadFromStorage(): Pick<ConsentState, 'consentGiven' | 'analytics' | 'advertising'> {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw) {
      const parsed: unknown = JSON.parse(raw);
      if (parsed && typeof parsed === 'object' && 'consentGiven' in parsed) {
        const data = parsed as Record<string, unknown>;
        return {
          consentGiven: data.consentGiven === true,
          analytics: data.analytics === true,
          advertising: data.advertising === true,
        };
      }
    }
  } catch {
    // Corrupted storage — treat as no consent given
  }
  return { consentGiven: false, analytics: false, advertising: false };
}

function persistToStorage(state: Pick<ConsentState, 'consentGiven' | 'analytics' | 'advertising'>): void {
  localStorage.setItem(
    STORAGE_KEY,
    JSON.stringify({
      consentGiven: state.consentGiven,
      analytics: state.analytics,
      advertising: state.advertising,
    }),
  );
}

const initial = loadFromStorage();

export const useConsentStore = create<ConsentState>((set) => ({
  consentGiven: initial.consentGiven,
  analytics: initial.analytics,
  advertising: initial.advertising,

  acceptAll: () => {
    const next = { consentGiven: true, analytics: true, advertising: true };
    persistToStorage(next);
    set(next);
  },

  rejectAll: () => {
    const next = { consentGiven: true, analytics: false, advertising: false };
    persistToStorage(next);
    set(next);
  },

  acceptEssentialOnly: () => {
    const next = { consentGiven: true, analytics: false, advertising: false };
    persistToStorage(next);
    set(next);
  },

  updatePreferences: (prefs) =>
    set((state) => {
      const next = {
        consentGiven: true,
        analytics: prefs.analytics ?? state.analytics,
        advertising: prefs.advertising ?? state.advertising,
      };
      persistToStorage(next);
      return next;
    }),

  resetConsent: () => {
    localStorage.removeItem(STORAGE_KEY);
    set({ consentGiven: false, analytics: false, advertising: false });
  },
}));
